import { Stepper, Step, StepLabel, Box, Stack, Link } from '@mui/material';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  ApiWorkflowSubmittedContent,
  Button,
} from '@verantos-dev/shared-components';
import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

import ConceptGroupNameInput from '../components/ConceptGroupNameInput';
import { ConceptsUploadStep } from '../components/ConceptsUploadStep';
import { ErrorMessage } from '../components/ErrorMessage';
import { ValidationResultsStep } from '../components/ValidationResultsStep';
import { useConceptGroupWorkflowDialog } from '../hooks/useConceptGroupWorkflowDialog';

interface InitialData {
  conceptGroupName: string;
  concepts: IsValidCreateUploadResponse[];
  filterKey?: string;
  previewId?: string;
  totalCount?: number;
  domainBreakdown?: Record<string, number>;
}

interface ConceptGroupWorkflowDialogProps {
  onClose: () => void;
  projectId?: string;
  onSuccess?: (conceptGroupId: string) => void;
  initialData?: InitialData;
}

export const ConceptGroupWorkflowDialog: FC<ConceptGroupWorkflowDialogProps> = ({
  onClose,
  projectId,
  onSuccess,
  initialData,
}) => {
  const intl = useIntl();
  const {
    conceptGroupName,
    onConceptGroupNameChange,
    selectedFile,
    onFileSelect,
    nameValidationError,
    nameValidationStatus,

    activeStep,
    steps,
    isValidForSubmit,
    concepts,
    fileWarnings,
    onNext,
    onBack,
    isLoading,
    validationError,
    creationError,
    isSuccess,
    handleDialogClose,
    previewId,
    totalCount,
    domainBreakdown,
  } = useConceptGroupWorkflowDialog(projectId, onSuccess, initialData);

  // Enhanced close handler that queues event modals if needed
  const handleClose = () => {
    handleDialogClose();
    onClose();
  };

  return (
    <ApiWorkflowDialog
      id="create-concept-group-dialog"
      title={intl.formatMessage({ id: 'conceptGroup.dialog.title' })}
      displayButtons={false}
      size={isSuccess ? 'medium' : 'full'}
      onClose={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {!isSuccess && (
        <>
          {/* Stepper */}
          <Stack direction={'column'} gap={4} mb={6}>
            {(validationError || creationError) && (
              <ErrorMessage validationError={validationError} creationError={creationError} />
            )}
            <Box px={8}>
              <Stepper activeStep={activeStep}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Stack>

          {/* Concept group name input */}
          <Box
            py={2}
            sx={{
              display: 'flex',
              justifyContent: activeStep === 0 ? 'center' : 'flex-start',
            }}
          >
            <ConceptGroupNameInput
              conceptGroupName={conceptGroupName}
              onConceptGroupNameChange={onConceptGroupNameChange}
              nameValidationError={nameValidationError}
              nameValidationStatus={nameValidationStatus}
              isLoading={isLoading}
            />
          </Box>

          {/* Concept group steps */}
          <Stack alignItems={'center'}>
            {activeStep === 0 && (
              <ConceptsUploadStep
                selectedFile={selectedFile}
                onFileSelect={onFileSelect}
                isLoading={isLoading}
              />
            )}
            {activeStep === 1 && (
              <ValidationResultsStep
                concepts={concepts}
                conceptGroupName={conceptGroupName}
                warnings={fileWarnings}
                previewId={previewId}
                totalCount={totalCount}
                domainBreakdown={domainBreakdown}
              />
            )}
          </Stack>

          {/* Actions */}
          <Stack flex={1} justifyContent={'space-between'} direction={'row'} pb={2} pt={6}>
            <Button
              id="create-concept-group-dialog-cancel"
              btnType="text"
              displayLabel={
                activeStep === 0
                  ? intl.formatMessage({ id: 'conceptGroup.dialog.cancel' })
                  : intl.formatMessage({ id: 'conceptGroup.dialog.back' })
              }
              onClickHandler={activeStep === 0 ? handleClose : onBack}
              disabled={isLoading}
            ></Button>
            <Stack direction={'row'} gap={4}>
              {activeStep === 0 ? (
                <Button
                  id="create-concept-group-dialog-download-template"
                  btnType="secondary"
                  displayLabel={intl.formatMessage({ id: 'conceptGroup.dialog.downloadTemplate' })}
                  component={Link}
                  href="/assets/concept-group-file-upload-template.xlsx"
                  rel="noopener noreferrer"
                  disabled={isLoading}
                ></Button>
              ) : null}
              <Button
                id="create-concept-group-dialog-upload"
                displayLabel={
                  activeStep === 0
                    ? intl.formatMessage({ id: 'conceptGroup.dialog.upload' })
                    : intl.formatMessage({ id: 'conceptGroup.dialog.title' })
                }
                disabled={!isValidForSubmit}
                onClickHandler={onNext}
                waitButton={isLoading}
              ></Button>
            </Stack>
          </Stack>
        </>
      )}
      {isSuccess ? (
        <ApiWorkflowSubmittedContent
          submittedTitle={intl.formatMessage({ id: 'conceptGroup.dialog.success.title' })}
          submittedText={intl.formatMessage(
            { id: 'conceptGroup.dialog.success.text' },
            {
              conceptGroupName,
            }
          )}
          onClose={handleClose}
          hideDisplayButtonsOnSuccess={true}
          successCountdownTime={3}
        />
      ) : null}
    </ApiWorkflowDialog>
  );
};
